import type { PDFDocumentProxy } from 'pdfjs-dist';

/** Bedrock の base64 入力上限を超えない、変換前バイナリの上限 */
export const MAX_BLOOD_TEST_IMAGE_BYTES = Math.floor(3.75 * 1024 * 1024);
export const MAX_BLOOD_TEST_PDF_BYTES = Math.floor(4.5 * 1024 * 1024);

const MIN_IMAGE_LONG_SIDE = 640;
const MIN_PDF_PAGE_LONG_SIDE = 520;
const MAX_RESIZE_ATTEMPTS = 8;

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('画像の圧縮に失敗しました'));
        }
      },
      'image/jpeg',
      quality
    );
  });
}

function replaceExtension(name: string, extension: string): string {
  const base = name.replace(/\.[^./\\]+$/, '');
  return `${base || 'blood-test'}${extension}`;
}

type DecodedImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
};

/** createImageBitmap 非対応ブラウザでは HTMLImageElement にフォールバックする */
async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      dispose: () => bitmap.close(),
    };
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        dispose: () => URL.revokeObjectURL(url),
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name} を画像として読み込めませんでした`));
    };
    image.src = url;
  });
}

/**
 * 画像全体の縦横比を保ったままJPEGへ再エンコードする。
 * 帳票の文字を優先し、最初は高解像度・高品質から開始して必要な場合だけ段階的に縮小する。
 */
async function resizeImageForAnalysis(file: File): Promise<File> {
  if (file.size <= MAX_BLOOD_TEST_IMAGE_BYTES) return file;

  const decoded = await decodeImage(file);
  try {
    const sourceLongSide = Math.max(decoded.width, decoded.height);
    if (sourceLongSide <= 0) throw new Error(`${file.name} の画像サイズを取得できませんでした`);

    let targetLongSide = Math.min(sourceLongSide, 3200);
    let quality = 0.9;
    let lastBlob: Blob | null = null;

    for (let attempt = 0; attempt < MAX_RESIZE_ATTEMPTS; attempt += 1) {
      const scale = Math.min(1, targetLongSide / sourceLongSide);
      const width = Math.max(1, Math.round(decoded.width * scale));
      const height = Math.max(1, Math.round(decoded.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('画像処理を開始できませんでした');

      // 透過画像も採血結果票として読みやすい白背景に統一する。
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(decoded.source, 0, 0, width, height);
      lastBlob = await canvasToJpeg(canvas, quality);
      canvas.width = 1;
      canvas.height = 1;

      if (lastBlob.size <= MAX_BLOOD_TEST_IMAGE_BYTES) {
        return new File([lastBlob], replaceExtension(file.name, '.jpg'), {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });
      }

      const sizeRatio = Math.sqrt(MAX_BLOOD_TEST_IMAGE_BYTES / lastBlob.size);
      targetLongSide = Math.max(
        MIN_IMAGE_LONG_SIDE,
        Math.floor(targetLongSide * Math.min(0.88, sizeRatio * 0.94))
      );
      quality = Math.max(0.42, quality - 0.07);
    }

    if (lastBlob && lastBlob.size <= MAX_BLOOD_TEST_IMAGE_BYTES) {
      return new File([lastBlob], replaceExtension(file.name, '.jpg'), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
    }
    throw new Error(`${file.name} を3.75MB以下に圧縮できませんでした`);
  } finally {
    decoded.dispose();
  }
}

async function renderCompressedPdf(
  source: PDFDocumentProxy,
  targetLongSide: number,
  jpegQuality: number,
  PDFDocument: typeof import('pdf-lib').PDFDocument
): Promise<Uint8Array> {
  const output = await PDFDocument.create();

  for (let pageNumber = 1; pageNumber <= source.numPages; pageNumber += 1) {
    const sourcePage = await source.getPage(pageNumber);
    const baseViewport = sourcePage.getViewport({ scale: 1 });
    const baseLongSide = Math.max(baseViewport.width, baseViewport.height);
    const renderScale = Math.max(0.1, targetLongSide / baseLongSide);
    const viewport = sourcePage.getViewport({ scale: renderScale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('PDFの画像処理を開始できませんでした');

    await sourcePage.render({ canvas, viewport, background: '#ffffff' }).promise;
    const jpeg = await canvasToJpeg(canvas, jpegQuality);
    const embedded = await output.embedJpg(await jpeg.arrayBuffer());
    const outputPage = output.addPage([baseViewport.width, baseViewport.height]);
    outputPage.drawImage(embedded, {
      x: 0,
      y: 0,
      width: baseViewport.width,
      height: baseViewport.height,
    });

    sourcePage.cleanup();
    canvas.width = 1;
    canvas.height = 1;
  }

  return output.save({ useObjectStreams: true, addDefaultPage: false });
}

/** PDFの全ページを画像として再構成し、ページ順と縦横比を保って容量を下げる */
async function resizePdfForAnalysis(file: File): Promise<File> {
  if (file.size <= MAX_BLOOD_TEST_PDF_BYTES) return file;

  const [pdfjs, { PDFDocument }] = await Promise.all([import('pdfjs-dist'), import('pdf-lib')]);
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url
    ).toString();
  }

  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const source = await loadingTask.promise;
  try {
    // ページ数が多いPDFは、初回から1ページあたりの画素数を抑えて再試行回数を減らす。
    let targetLongSide = Math.max(
      1100,
      Math.round(2600 / Math.sqrt(Math.max(1, source.numPages / 3)))
    );
    let quality = 0.88;

    for (let attempt = 0; attempt < MAX_RESIZE_ATTEMPTS; attempt += 1) {
      const bytes = await renderCompressedPdf(source, targetLongSide, quality, PDFDocument);
      if (bytes.byteLength <= MAX_BLOOD_TEST_PDF_BYTES) {
        // pdf-lib の ArrayBufferLike を通常の ArrayBuffer へコピーして File に渡す。
        const fileBytes = new Uint8Array(bytes.byteLength);
        fileBytes.set(bytes);
        return new File([fileBytes.buffer], replaceExtension(file.name, '.pdf'), {
          type: 'application/pdf',
          lastModified: file.lastModified,
        });
      }

      const sizeRatio = Math.sqrt(MAX_BLOOD_TEST_PDF_BYTES / bytes.byteLength);
      targetLongSide = Math.max(
        MIN_PDF_PAGE_LONG_SIDE,
        Math.floor(targetLongSide * Math.min(0.88, sizeRatio * 0.94))
      );
      quality = Math.max(0.38, quality - 0.07);
    }

    throw new Error(
      `${file.name} を4.5MB以下に圧縮できませんでした。ページ数を減らして再度お試しください`
    );
  } finally {
    await source.destroy();
  }
}

/** 解析APIへ送る各ファイルを、Bedrock向けの種別別上限以下に揃える */
export async function prepareBloodTestFilesForUpload(files: File[]): Promise<File[]> {
  const prepared: File[] = [];
  for (const file of files) {
    const resized =
      file.type === 'application/pdf'
        ? await resizePdfForAnalysis(file)
        : await resizeImageForAnalysis(file);
    const maxBytes =
      resized.type === 'application/pdf'
        ? MAX_BLOOD_TEST_PDF_BYTES
        : MAX_BLOOD_TEST_IMAGE_BYTES;
    if (resized.size > maxBytes) {
      throw new Error(`${file.name} を解析用の上限サイズまで圧縮できませんでした`);
    }
    prepared.push(resized);
  }
  return prepared;
}
